// The daily backup, from whichever device has it turned on: once a day the
// open app zips every note and hands the zip to the site's server
// (src/server), which emails it. Its settings are this device's own, never
// synced; the password sits next to notes that are on this device in the
// clear already.
import type { Note } from '@/app/db/repo';
import { makeArchive } from './archive';

export interface BackupSettings {
  // locks the zip
  password: string;
  // lets this device through the server's door
  secret: string;
}

export type BackupStatus =
  | { ok: true, at: string, notes: number }
  | { ok: false, at: string, error: string };

export const BACKUP_EVERY_MS = 24 * 60 * 60 * 1000;

const SETTINGS_KEY = 'backup';
const STATUS_KEY = 'backup-status';
const LAST_KEY = 'backup-last';

// storage can be off, or throw, in a private window
const read = <T>(key: string): T | null => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export const backupSettings = () => read<BackupSettings>(SETTINGS_KEY);

// null turns backups off on this device
export const saveBackupSettings = (settings: BackupSettings | null) =>
  write(SETTINGS_KEY, settings);

export const backupStatus = () => read<BackupStatus>(STATUS_KEY);

// when the last backup went out; a failed one doesn't count
const lastBackup = () => read<string>(LAST_KEY);

export const isDue = (last: string | null, now: Date) =>
  last === null || now.getTime() - Date.parse(last) >= BACKUP_EVERY_MS;

// the local date, which is what the day of a backup means to its reader
export const localDate = (at: Date) =>
  [
    at.getFullYear(),
    String(at.getMonth() + 1).padStart(2, '0'),
    String(at.getDate()).padStart(2, '0'),
  ].join('-');

export const sendBackup = async (
  zip: Uint8Array<ArrayBuffer>,
  secret: string,
  date: string,
) => {
  const response = await fetch(`/api/backup?date=${date}`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${secret}`,
      'content-type': 'application/zip',
    },
    body: zip,
  });
  if (!response.ok) {
    throw new Error(
      `${response.status} ${(await response.text()) || response.statusText}`,
    );
  }
};

// Backs up now, whether or not one is due; the outcome is kept for the
// settings to show.
export const backUp = async (
  notes: () => Promise<Note[]>,
  settings: BackupSettings,
): Promise<BackupStatus> => {
  const at = new Date();
  const status: BackupStatus = await notes()
    .then(async all => {
      const zip = await makeArchive(all, settings.password);
      await sendBackup(zip, settings.secret, localDate(at));
      return { ok: true as const, at: at.toISOString(), notes: all.length };
    })
    .catch(e => ({
      ok: false as const,
      at: at.toISOString(),
      error: e instanceof Error ? e.message : String(e),
    }));
  write(STATUS_KEY, status);
  if (status.ok) write(LAST_KEY, status.at);
  return status;
};

// Backs up if this device should and a day has passed. One tab at a time,
// and a tab that waited checks again, so two open tabs send one email.
export const backUpIfDue = (notes: () => Promise<Note[]>) =>
  navigator.locks.request(
    'notes-backup',
    { ifAvailable: true },
    async lock => {
      const settings = backupSettings();
      if (!lock || !settings || !isDue(lastBackup(), new Date())) return null;
      return backUp(notes, settings);
    },
  );
